import { useCallback } from 'react';
import dayjs from 'dayjs';
import { isEmpty, unionBy } from 'lodash';
import {
  AllPlanScheduleType,
  ChatAdjustmentCTA,
  StreamChatSessionType,
  SuggestedTaskItem,
} from '@demind-inc/core';
import { useSetRecoilState } from 'recoil';
import {
  AssistantChatMessage,
  ChatSuggestedEvent,
  UnifiedChatRichContextEntry,
} from '../../../types';
import { ChatState } from '../types';
import {
  transformSuggestedTasksToEvents,
  transformAdjustmentCTAToSuggestedBlocks,
  transformSleepMealToSuggestedBlocks,
  getDateForTimeAfterMidnight,
  filterWorkoutScheduleBlocksWithRichContextCtas,
} from '../../../helpers/';
import {
  discardPendingAllPlanSuggestionsByType,
  hasWorkoutAllPlanReplacementResult,
  persistDiscardedPendingAllPlanSuggestionsByType,
  shouldDiscardCurrentAllPlanSuggestionsForDate,
} from '../../../../helpers/allPlanSuggestions.helpers';
import { SyncChatResultOutput } from '../../../api';
import { suggestAllAIPlanStateAtom } from '../../../recoil';
import { hasCtasAdjustment, hasCtaAdjustment } from './helpers';

interface UseFormattedResultHandlerParams {
  state: ChatState;
  updateState: (updates: Partial<ChatState>) => void;
  addMessage: (message: AssistantChatMessage) => void;
  setTemporalSuggestedEvents: (
    events: ChatSuggestedEvent[] | ((prevEvents: ChatSuggestedEvent[]) => ChatSuggestedEvent[])
  ) => void;
  /** Called with the messageId of the CTA message when it is added */
  onCtaMessageCreated?: (messageId: string) => void;
  /** Called with the CTA blocks when they are first created */
  onCtaBlocksCreated?: (blocks: ChatSuggestedEvent[]) => void;
}

interface FormattedResult {
  sessions?: AssistantChatMessage[];
  suggestedTasks?: SuggestedTaskItem[];
  sleepData?: SyncChatResultOutput['sleepData'];
  mealData?: SyncChatResultOutput['mealData'];
  detailedExplanation?: string;
  cta?: ChatAdjustmentCTA; // will be deprecated
  ctas?: ChatAdjustmentCTA[]; // use v2 general chat (multi-day)
}

export const useFormattedResultHandler = ({
  state,
  updateState,
  addMessage,
  setTemporalSuggestedEvents,
  onCtaMessageCreated,
  onCtaBlocksCreated,
}: UseFormattedResultHandlerParams) => {
  const setSuggestAllAIPlanState = useSetRecoilState(suggestAllAIPlanStateAtom);

  const discardPendingAllPlanSuggestions = useCallback(
    (scheduleTypes: AllPlanScheduleType[], date?: string) => {
      if (!shouldDiscardCurrentAllPlanSuggestionsForDate(date)) {
        return;
      }

      setSuggestAllAIPlanState((prev) =>
        discardPendingAllPlanSuggestionsByType(prev, scheduleTypes)
      );
      persistDiscardedPendingAllPlanSuggestionsByType(scheduleTypes, date);
    },
    [setSuggestAllAIPlanState]
  );

  const handleSleepResult = useCallback(
    (formattedResult: FormattedResult, date?: string) => {
      if (!formattedResult.sleepData) return;

      const { sleepTime } = formattedResult.sleepData;
      const detailedExplanationForSleep = formattedResult.detailedExplanation;

      // Transform sleep data to suggested blocks
      const suggestedBlocks = transformSleepMealToSuggestedBlocks(
        formattedResult.sleepData,
        undefined,
        date
      );

      // Update state with sleep context and target event
      updateState({
        userContext: {
          ...state.userContext,
          sleep_detailed_explanation: detailedExplanationForSleep,
        },
        targetEventOnCalendar: {
          date: getDateForTimeAfterMidnight(sleepTime, dayjs(date)).format('YYYY-MM-DD'),
          time: sleepTime,
        },
      });

      // Add message with suggested blocks for check-on-calendar
      if (suggestedBlocks.length > 0) {
        const sleepMessage: AssistantChatMessage = {
          messageId: `sleep_${Date.now()}`,
          content: '',
          role: 'assistant',
          sentAt: new Date().toISOString(),
          suggestedBlocks: suggestedBlocks,
        };
        addMessage(sleepMessage);
      }
    },
    [state.userContext, updateState, addMessage]
  );

  const handleMealResult = useCallback(
    (formattedResult: FormattedResult, date?: string) => {
      if (!formattedResult.mealData) return;

      const { breakfast, lunch, dinner } = formattedResult.mealData;
      const detailedExplanationForMeal = formattedResult.detailedExplanation;

      // Transform meal data to suggested blocks
      const suggestedBlocks = transformSleepMealToSuggestedBlocks(
        undefined,
        formattedResult.mealData,
        date
      );

      // Determine target time (prefer breakfast, then lunch, then dinner)
      const targetTime =
        breakfast?.start || lunch?.start || dinner?.start || dayjs().format('HH:mm');

      // Update state with meal context and target event
      updateState({
        userContext: {
          ...state.userContext,
          meals_detailed_explanation: detailedExplanationForMeal,
        },
        targetEventOnCalendar: {
          date: date || dayjs().format('YYYY-MM-DD'),
          time: targetTime,
        },
      });

      // Add message with suggested blocks for check-on-calendar
      if (suggestedBlocks.length > 0) {
        const mealMessage: AssistantChatMessage = {
          messageId: `meal_${Date.now()}`,
          content: '',
          role: 'assistant',
          sentAt: new Date().toISOString(),
          suggestedBlocks: suggestedBlocks,
        };
        addMessage(mealMessage);
      }
    },
    [state.userContext, updateState, addMessage]
  );

  const handleTaskResult = useCallback(
    (formattedResult: FormattedResult, date?: string) => {
      const { suggestedTasks, detailedExplanation: detailedExplanationForTask } = formattedResult;

      const hasSuggestedTasks = suggestedTasks?.length > 0;

      if (hasSuggestedTasks) {
        const suggestedTaskEvents = transformSuggestedTasksToEvents(
          suggestedTasks,
          date || dayjs().format('YYYY-MM-DD'),
          state.chatSessionId
        );

        setTemporalSuggestedEvents(suggestedTaskEvents);

        // Add message with suggested blocks for check-on-calendar
        const taskMessage: AssistantChatMessage = {
          messageId: `task_${Date.now()}`,
          content: '',
          role: 'assistant',
          sentAt: new Date().toISOString(),
          suggestedBlocks: suggestedTaskEvents,
        };
        addMessage(taskMessage);

        // Update state with task context and target event
        updateState({
          temporalSuggestedEvents: unionBy(
            state.temporalSuggestedEvents,
            suggestedTaskEvents,
            'linkedMessageId'
          ),
          targetEventOnCalendar: {
            date: dayjs(date || dayjs().format('YYYY-MM-DD'))
              .startOf('day')
              .add(suggestedTasks?.[0]?.suggestedStartDayOffset ?? 0, 'day')
              .format('YYYY-MM-DD'),
            time: suggestedTasks?.[0]?.suggestedStartTime,
          },
          userContext: {
            ...state.userContext,
            task_detailed_explanation: detailedExplanationForTask,
          },
        });
      } else {
        // Update state with just the task context if no suggested tasks
        updateState({
          userContext: {
            ...state.userContext,
            task_detailed_explanation: detailedExplanationForTask,
          },
        });
      }
    },
    [
      state.userContext,
      state.temporalSuggestedEvents,
      state.chatSessionId,
      updateState,
      addMessage,
      setTemporalSuggestedEvents,
    ]
  );

  // v2 general chat only supports multi-day
  const handleGeneralResult = useCallback(
    (
      formattedResult: FormattedResult,
      adjustmentRichContexts?: UnifiedChatRichContextEntry[],
      queryRichContexts?: UnifiedChatRichContextEntry[]
    ) => {
      const hasCta = formattedResult.ctas
        ? hasCtasAdjustment(formattedResult.ctas)
        : hasCtaAdjustment(formattedResult.cta);

      // Handle CTA buttons if present
      if (hasCta) {
        const richContextEntriesForFiltering = [
          ...(queryRichContexts ?? []),
          ...(adjustmentRichContexts ?? []),
        ];
        const suggestedBlocks = filterWorkoutScheduleBlocksWithRichContextCtas(
          transformAdjustmentCTAToSuggestedBlocks(formattedResult.ctas || [formattedResult.cta]),
          richContextEntriesForFiltering
        );
        const temporalCalendarPreviewEvents = suggestedBlocks.filter(
          (block) => block.action === 'setEvent' && !block.linkedMessageId
        );

        if (temporalCalendarPreviewEvents.length > 0) {
          setTemporalSuggestedEvents((prevEvents) => {
            const replacingEventIds = new Set(
              temporalCalendarPreviewEvents.map((event) => event.eventId)
            );
            const filteredPrevEvents = prevEvents.filter(
              (event) => !replacingEventIds.has(event.eventId)
            );

            return [...filteredPrevEvents, ...temporalCalendarPreviewEvents];
          });
        }

        if (!suggestedBlocks.length && !adjustmentRichContexts?.length) {
          return;
        }

        const ctaMessageId = `cta_${Date.now()}`;
        const ctaMessage: AssistantChatMessage = {
          messageId: ctaMessageId,
          content: '',
          role: 'assistant',
          sentAt: new Date().toISOString(),
          suggestedBlocks: suggestedBlocks.length > 0 ? suggestedBlocks : undefined,
          richContexts: adjustmentRichContexts?.length ? adjustmentRichContexts : undefined,
        };
        addMessage(ctaMessage);
        onCtaMessageCreated?.(ctaMessageId);
        onCtaBlocksCreated?.(suggestedBlocks);
      }
    },
    [addMessage, setTemporalSuggestedEvents, onCtaMessageCreated, onCtaBlocksCreated]
  );

  const handleFormattedResult = useCallback(
    (
      formattedResult: FormattedResult,
      sessionType: StreamChatSessionType,
      date?: string,
      adjustmentRichContexts?: UnifiedChatRichContextEntry[],
      queryRichContexts?: UnifiedChatRichContextEntry[]
    ) => {
      switch (sessionType) {
        case 'sleep':
          handleSleepResult(formattedResult, date);
          break;
        case 'meal':
          handleMealResult(formattedResult, date);
          break;
        case 'task':
          discardPendingAllPlanSuggestions(['todo'], date);
          handleTaskResult(formattedResult, date);
          break;
        case 'general':
          if (hasWorkoutAllPlanReplacementResult(formattedResult, adjustmentRichContexts)) {
            discardPendingAllPlanSuggestions(['workout'], date);
          }
          handleGeneralResult(formattedResult, adjustmentRichContexts, queryRichContexts);
          break;
        case 'recommendation':
          handleGeneralResult(formattedResult, adjustmentRichContexts, queryRichContexts);
          break;
        default:
          if (String(sessionType) === 'workout' || String(sessionType) === 'freeText') {
            discardPendingAllPlanSuggestions(['workout'], date);
          }
          handleGeneralResult(formattedResult, adjustmentRichContexts, queryRichContexts);
          break;
      }
    },
    [
      handleSleepResult,
      handleMealResult,
      handleTaskResult,
      handleGeneralResult,
      discardPendingAllPlanSuggestions,
    ]
  );

  return {
    handleFormattedResult,
    handleSleepResult,
    handleMealResult,
    handleTaskResult,
    handleGeneralResult,
  };
};
